import React from 'react';

import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import MomentUtils from '@date-io/moment';

import {
  MuiPickersUtilsProvider,
  KeyboardTimePicker,
  KeyboardDatePicker,
  TimePicker,
} from '@material-ui/pickers';
import Dialog from '@material-ui/core/Dialog';
import {
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import { X } from 'phosphor-react';
import moment from 'moment';
import { connect } from 'react-redux';
import { isValidTime } from '../../../lib/helpers';
import LogoLoader from '../LogoLoader';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    avatar: {
      backgroundColor: 'blue',
      color: 'blue',
    },
    paper: {
      borderRadius: 16,
      padding: 16,
      background: '#FFFFFF',
      width: 255,
    },
    content: {
      padding: 0,
      paddingTop: '0 !important',
    },
    title: {
      fontFamily: 'Sharp Grotesk Semibold !important',
      fontSize: '14px',
      color: '#000000',
    },
    closeButton: {
      position: 'absolute',
      top: 5,
      right: 10,
      padding: 10,
    },
    labelText: {
      fontSize: 10,
      fontWeight: 'bold',
      marginTop: 15,
    },
    dateText: {
      fontSize: 12,
      color: '#000000',
    },
    formControl: {
      margin: 0,
      minWidth: 200,
    },
    selectField: {
      height: 32,
      borderRadius: 4,
    },
    selectFilled: {
      padding: 5,
    },
    selectRoot: {},
    scheduleButton: {
      background: '#195A50',
      borderRadius: 24,
      height: 44,
      width: 130,
      '&:hover': {
        background: '#2e9283',
      },
      '&:disabled': {
        background: 'grey',
      },
    },
    deleteButton: {
      backgroundColor: 'transparent',
      borderRadius: 24,
      color: '#822841',
      height: 44,
      fontSize: 12,
      textTransform: 'none',
    },
    buttonText: {
      color: '#ffffff',
      fontSize: 12,
      fontWeight: 'bold',
      margin: '0 10px',
      textTransform: 'none',
    },
    buttonContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginTop: 25,
    },
  }),
);

interface Props {
  deleteLoading: boolean;
  deleteShowing: (isOpenHouse?: boolean) => void;
  isEditing?: boolean;
  onClose: Function;
  open: boolean;
  scheduleShowing: (time: moment.Moment) => void;
  selectedDate: Date | undefined;
  submitLoading: boolean;
  updateShowing: (time: moment.Moment) => void;
}

export function TourDialog({
  onClose,
  selectedDate,
  open,
  scheduleShowing,
  updateShowing,
  isEditing,
  submitLoading,
  deleteShowing,
  deleteLoading,
}: Props) {
  const [selectedTime, setTime] = React.useState(moment());
  const [error, setError] = React.useState(false);
  const [time, setFomattedTime] = React.useState(
    moment(selectedTime, 'HH:mm A'),
  );
  const classes = useStyles();
  React.useEffect(() => {
    if (selectedDate) {
      setTime(moment(selectedDate));
      if (isValidTime(selectedDate)) {
        setError(false);
      } else {
        setError(true);
      }
    }
  }, [selectedDate]);

  React.useEffect(() => {
    setFomattedTime(moment(selectedTime, 'HH:mm A'));
  }, [selectedTime]);

  const handleClose = React.useCallback(() => {
    onClose();
  }, []);

  const handleChange = React.useCallback((date: any) => {
    setTime(date);
    if (isValidTime(date)) {
      setError(false);
    } else {
      setError(true);
    }
  }, []);

  const handleSubmit = React.useCallback(() => {
    if (isEditing) {
      updateShowing(time);
    } else {
      scheduleShowing(time);
    }
  }, [time]);

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="tour-dialog"
      classes={{ paper: classes.paper }}
      open={open}
    >
      <DialogContent classes={{ root: classes.content }}>
        <Typography className={classes.title}>
          {isEditing ? 'Edit Tour' : 'Schedule Tour'}
        </Typography>
        <IconButton
          className={classes.closeButton}
          disabled={submitLoading || deleteLoading}
          onClick={handleClose}
        >
          <X size={16} color="#2D2332" weight="regular" />
        </IconButton>

        <p className={classes.labelText}>Date</p>
        <p className={classes.dateText}>{moment(selectedDate).format('ll')}</p>

        <p className={classes.labelText}>Pick a time:</p>

        <MuiPickersUtilsProvider utils={MomentUtils}>
          <TimePicker
            views={['hours', 'minutes']}
            id="time-picker"
            minutesStep={5}
            inputVariant="filled"
            fullWidth
            className="showings-calendar"
            value={selectedTime}
            onChange={handleChange}
            style={{ padding: 5 }}
          />
          <span style={{ color: 'red', fontSize: 10 }}>
            {error ? 'Select between 5:00 AM and 10:00 PM' : ''}
          </span>
        </MuiPickersUtilsProvider>

        <div className={classes.buttonContainer}>
          <Button
            className={classes.scheduleButton}
            classes={{ label: classes.buttonText }}
            onClick={handleSubmit}
            disabled={deleteLoading || submitLoading || error}
          >
            {submitLoading ? (
              <LogoLoader imageProps={{ height: 40, width: 40 }} />
            ) : isEditing ? (
              'Update Tour'
            ) : (
              'Schedule Tour'
            )}
          </Button>

          {isEditing && (
            <Button
              disabled={deleteLoading || submitLoading}
              onClick={() => deleteShowing(false)}
              className={classes.deleteButton}
            >
              {deleteLoading ? (
                <LogoLoader imageProps={{ height: 40, width: 40 }} />
              ) : (
                'Cancel Tour'
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const mapStateToProps = (state: any) => ({
  submitLoading: state.showings.showingsSubmitLoading,
  deleteLoading: state.showings.showingDeleteLoading,
});

export default connect(mapStateToProps)(TourDialog);
